import type { FC, MouseEventHandler, WheelEventHandler } from "react"
import { useMemo, memo, useEffect, useRef } from "react"

import { AnimatePresence, motion } from "framer-motion"
import { useRecoilValue, useSetRecoilState } from "recoil"
import styled from "styled-components"

import { ReactComponent as LocationPin } from "~/assets/svg/powur-pin.svg"
import { ErrorBoundary } from "~/components/ErrorBoundary"
import { currentUserIsDesignRequestUserState } from "~/containers/Proposal/state/user/user-state"
import { useSetMousePosition } from "~/hooks/useMousePosition"
import { useSetStudioCenterPosition } from "~/hooks/useStudioCenterPosition"
import { useTriggerPreventionToast } from "~/hooks/useTriggerPreventionToast"
import IS_DEV from "~/utils/isDev"
import IS_QA from "~/utils/isQa"

import { StudioDebugVisualization } from "./Debug/Visualizations"
import { DesignPreventionToast } from "./DesignPreventionToast"
import type { Cursor } from "./Elements/CURSORS"
import { getCursorFile } from "./Elements/CURSORS"
import { Edge } from "./Elements/Edge"
import { InstallAreas } from "./Elements/InstallAreas"
import { Node } from "./Elements/Node"
import { Panels } from "./Elements/Panels"
import IconBar from "./IconBar/index"
import ImageGroup from "./ImageGroup"
import { DragBox } from "./Overlays/DragBox"
import { MoveBox } from "./Overlays/MoveBox"
import { PanRectangle } from "./Overlays/PanRectangle"
import { PreviewEdge } from "./Overlays/PreviewEdge"
import { PreviewPanel } from "./Overlays/PreviewPanel"
import { PreviewPanelMatrix } from "./Overlays/PreviewPanelMatrix"
import { Ruler } from "./Overlays/Ruler"
import { SystemInfoPanel } from "./SystemInfoPanel"
import { Toolbar } from "./Toolbar"
import { showDesignSwitcherState } from "../DesignSwitcher"
import { edgeIndex, nodeIndex } from "../state/elements"
import { cursorState, mouseHeldInStudioState } from "../state/elements/cursor"
import { lastNodeIdState } from "../state/elements/nodeSelection"
import { panelSelectionState } from "../state/elements/panels"
import {
  useDragSelect,
  useTryToDrawNode,
  useZoomAroundMouse,
} from "../state/elements/transactions"
import { useDragActivate } from "../state/elements/transactions/dragBox/dragActivate"
import { useDragCreate } from "../state/elements/transactions/dragBox/dragCreateMatrix"
import { useDragDesignateEdges } from "../state/elements/transactions/dragBox/dragDesignateEdges"
import { useDragRuler } from "../state/elements/transactions/dragBox/dragRuler"
import { centerOfImageState } from "../state/imageElements"
import { startPointState } from "../state/inputs"
import { areWeAfterPhase, phaseState } from "../state/phase"
import type { Tool } from "../state/tool"
import { toolState } from "../state/tool"
import { moveBoxPositionState, panState } from "../state/view"

interface LayerProps {
  cursor: Cursor
}

const Layer = styled.span<LayerProps>`
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  display: block;
  background-color: #bebebe;
  cursor: ${(props) => getCursorFile(props.cursor)};
`

const StudioFallback = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  * {
    padding: 5px;
  }
`

const Studio_Internal: FC = () => {
  const setMouseHeld = useSetRecoilState(mouseHeldInStudioState)
  const tool = useRecoilValue(toolState)
  const cursor = useRecoilValue(cursorState)
  const phase = useRecoilValue(phaseState)
  const currentUserIsDesignUser = useRecoilValue(
    currentUserIsDesignRequestUserState,
  )
  const triggerPreventionToast = useTriggerPreventionToast()

  const pointPositionRef = useRef(null)
  const mousePositionRef = useSetMousePosition<HTMLSpanElement>(`Studio`)
  const centerPositionRef = useSetStudioCenterPosition<HTMLSpanElement>(`Studio`)
  const centerOfImage = useRecoilValue(centerOfImageState)
  const moveBoxPosition = useRecoilValue(moveBoxPositionState)

  const pan = useRecoilValue(panState)
  const zoomAroundMouse = useZoomAroundMouse()

  const nodeIds = useRecoilValue(nodeIndex)
  const edgeIds = useRecoilValue(edgeIndex)
  const lastNodeId = useRecoilValue(lastNodeIdState)

  const tryToDrawNode = useTryToDrawNode()
  const hasStartPoint = Boolean(useRecoilValue(startPointState))
  const [startDragSelect, finishDragSelect] = useDragSelect()
  const [startDragCreate, finishDragCreate] = useDragCreate()
  const [startDragActivate, finishDragActivate] = useDragActivate()
  const [startDragDesignateEdge, finishDragDesignateEdge] =
    useDragDesignateEdges()
  const [startDragRuler, finishDragRuler] = useDragRuler()

  const isAfterOutline = useRecoilValue(areWeAfterPhase(`Outline`))
  const isAfterRoofAttributes = useRecoilValue(
    areWeAfterPhase(`Roof Attributes`),
  )
  const selectedPanelsSet = useRecoilValue(panelSelectionState)

  // const { resetRemote } = useDesign()

  useEffect(() => {
    mousePositionRef.current = pointPositionRef.current
    centerPositionRef.current = pointPositionRef.current
  }, [mousePositionRef, centerPositionRef])

  const mouseHeldHandler: MouseEventHandler<HTMLSpanElement> = (e): void => {
    if (e.type === `mousedown`) {
      setMouseHeld(true)
    } else if (e.type === `mouseup`) {
      setMouseHeld(false)
    }
  }

  const onWheel: WheelEventHandler<HTMLSpanElement> = (e) => {
    const { deltaY } = e
    if (deltaY > 0) {
      zoomAroundMouse((zoom) => (10 / 11) * zoom)
    } else {
      zoomAroundMouse((zoom) => Math.min((11 / 10) * zoom, 1300))
    }
  }

  const onMouseDownFor: Partial<Record<Tool, MouseEventHandler>> = {
    Select: startDragSelect,
    Draw: tryToDrawNode,
    PanelActivator: startDragActivate,
    PanelCreatorPortrait: startDragCreate,
    PanelCreatorLandscape: startDragCreate,
    EdgeDesignator: startDragDesignateEdge,
    Ruler: startDragRuler,
  }

  const onMouseUpFor: Partial<Record<Tool, MouseEventHandler>> = {
    Select: finishDragSelect,
    PanelActivator: finishDragActivate,
    PanelCreatorPortrait: finishDragCreate,
    PanelCreatorLandscape: finishDragCreate,
    EdgeDesignator: finishDragDesignateEdge,
    Ruler: finishDragRuler,
  }

  const selectedPanels = useMemo(
    () => [...selectedPanelsSet],
    [selectedPanelsSet],
  )

  const isDesignSwitcherOpen = useRecoilValue(showDesignSwitcherState)

  return (
    <ErrorBoundary
      Fallback={({ error }) => {
        return (
          <StudioFallback>
            <h3>Something went wrong</h3>
            <p>
              Reloading the page may fix this issue. If the issue persists,
              please use the reset button to reset the design, then reload.
            </p>
          </StudioFallback>
        )
      }}
    >
      <Layer
        data-testid="system-design-area"
        cursor={cursor}
        ref={pointPositionRef}
        onWheel={onWheel}
        onMouseDown={mouseHeldHandler}
        onMouseUp={mouseHeldHandler}
      >
        <DesignPreventionToast />
        {!nodeIds.length && (
          <LocationPin
            style={{
              position: `absolute`,
              zIndex: 10,
              top: pan.y - 25 + centerOfImage.y,
              left: pan.x - 20 + centerOfImage.x,
            }}
            onMouseOver={(e: {
              currentTarget: { style: { display: string } }
            }) => (e.currentTarget.style.display = `none`)}
            onMouseOut={(e: { currentTarget: { style: { display: string } } }) =>
              (e.currentTarget.style.display = `block`)
            }
            height="50px"
            width="2em"
          />
        )}

        <svg
          data-testid="system-design-svg"
          style={{
            position: `absolute`,
            top: pan.y,
            left: pan.x,
            background: `#ccc`,
            touchAction: `none`,
          }}
          height="8000px"
          width="8000px"
          onMouseDown={
            !currentUserIsDesignUser ? undefined : onMouseDownFor[tool]
          }
          onMouseUp={!currentUserIsDesignUser ? undefined : onMouseUpFor[tool]}
          onClick={currentUserIsDesignUser ? undefined : triggerPreventionToast}
        >
          <ImageGroup />
          <g
            style={{
              transform: `translate(${moveBoxPosition.x}px, ${moveBoxPosition.y}px)`,
            }}
          >
            {isAfterOutline || IS_DEV ? <InstallAreas /> : null}
            {isAfterRoofAttributes && <Panels />}
            {edgeIds.map((id) => (
              <Edge key={id} id={id} />
            ))}
            {phase === `Outline` &&
              nodeIds.map((id) => <Node key={id} id={id} />)}

            {hasStartPoint && <DragBox />}
            {tool === `Move` && <MoveBox />}
            {tool === `Pan` && <PanRectangle />}
            {tool === `Draw` && lastNodeId && <PreviewEdge />}
            {tool === `Ruler` && <Ruler />}
            {[`PanelCreatorPortrait`, `PanelCreatorLandscape`].includes(
              tool,
            ) && (
              <>
                <PreviewPanel />
                <PreviewPanelMatrix />
              </>
            )}
            {(IS_DEV || IS_QA) && <StudioDebugVisualization />}
            {/* <>
              {selectedPanels.map((id) => (
                <SnapLines key={id} id={id} />
              ))}
            </> */}
          </g>
        </svg>
        <AnimatePresence>
          {!isDesignSwitcherOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35 }}
            >
              <Toolbar />
              <SystemInfoPanel />
              <IconBar />
            </motion.div>
          )}
        </AnimatePresence>
      </Layer>
    </ErrorBoundary>
  )
}

export const Studio = memo(Studio_Internal)
